// Shared "start a new chat with this report" handoff. Used by
// ChatCTABlockRenderer and useHtmlReportBridge. Chat replays the payload from
// history.state on mount (see pendingMessage handling in chat.tsx).
import type { NavigateFunction } from 'react-router-dom';
import type { UIBlock } from '@/types/ui-blocks';
import type { CardBlock } from '@/hooks/useCards';

export interface ChatHandoffPayload {
  message: string;
  blocks?: UIBlock[];
  platformContext?: Record<string, unknown>;
  /** When set, the chat user-message bubble renders this card instead of
   *  plain text — mirrors the homepage card→chat handoff. */
  cardBlock?: CardBlock;
}

interface ChatWorkflowTriggerResponse {
  workflow_execution?: { id?: string };
}

export interface ChatHandoffDeps {
  triggerChatWorkflow: (input: {
    trigger_workflow: { workflow_instance_id: string };
  }) => Promise<ChatWorkflowTriggerResponse | undefined>;
  navigate: NavigateFunction;
  workflowInstanceId: string;
  payload: ChatHandoffPayload;
}

export function navigateToChatWithPayload(
  navigate: NavigateFunction,
  executionId: string,
  payload: ChatHandoffPayload
): void {
  navigate(`/ui/chat/${executionId}`, {
    state: {
      pendingMessage: payload.message,
      pendingBlocks: payload.blocks,
      pendingPlatformContext: payload.platformContext,
      pendingCardBlock: payload.cardBlock,
    },
  });
}

// Triggers the chat workflow without navigating. Use this when the caller
// needs to interleave other side-effects (e.g. posting an iframe response)
// between the trigger and the route change. Returns the new execution id, or
// null if the workflow trigger succeeded but the response carried no id.
// Mutation rejections propagate.
export async function startChatWorkflow({
  triggerChatWorkflow,
  workflowInstanceId,
}: Pick<ChatHandoffDeps, 'triggerChatWorkflow' | 'workflowInstanceId'>): Promise<string | null> {
  const response = await triggerChatWorkflow({
    trigger_workflow: { workflow_instance_id: workflowInstanceId },
  });
  return response?.workflow_execution?.id ?? null;
}

// Convenience wrapper: triggers the workflow and navigates on success. Used
// where there is nothing to do between the two steps.
export async function startChatHandoff({
  triggerChatWorkflow,
  navigate,
  workflowInstanceId,
  payload,
}: ChatHandoffDeps): Promise<string | null> {
  const executionId = await startChatWorkflow({ triggerChatWorkflow, workflowInstanceId });
  if (!executionId) return null;
  navigateToChatWithPayload(navigate, executionId, payload);
  return executionId;
}
